import { Check, Close } from '@icon-park/react';
import { memo, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Nullable } from '@didaskill/types/misc';
import { Button, Card, Typography } from '@didaskill/ui/design-system';
import { globalMessages } from '@didaskill/ui/intl';
import { ComponentSwitch } from '@didaskill/ui/utils';

import messages from './ExamRegistrationCard.messages';
import * as styles from './ExamRegistrationCard.styles';

export type ExamRegistrationCardProps = {
  /** Question asked to the student. */
  title: string;
  /** Student's answer, `null` until the student has answered. */
  isRegistered: Nullable<boolean>;
  /** Email address the student must send their registration proof to. */
  proofEmail: string;
  onAnswer?: (isRegistered: boolean) => void;
  onRequestClose?: () => void;
};

/**
 * Card asking a student whether they are registered to the exams, then thanking them.
 *
 * @param props - Component props.
 * @returns The exam registration card.
 */
const ExamRegistrationCardBase = ({
  title,
  isRegistered,
  proofEmail,
  onAnswer,
  onRequestClose,
}: ExamRegistrationCardProps): JSX.Element => {
  const intl = useIntl();

  return (
    <Card variant={Card.VARIANTS.NEUTRAL} css={styles.examRegistrationCard}>
      <Card.Body css={styles.examRegistrationCardBody}>
        <ComponentSwitch value={isRegistered}>
          <ComponentSwitch.Item value={null}>
            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD} css={styles.title}>
              {title}
            </Typography>

            <div css={styles.actions}>
              <Button
                variant={Button.VARIANTS.SUCCESS}
                size={Button.SIZES.SMALL}
                icon={<Check />}
                onClick={() => onAnswer?.(true)}
              >
                <FormattedMessage {...messages.YES} />
              </Button>

              <Button
                variant={Button.VARIANTS.DANGER}
                size={Button.SIZES.SMALL}
                icon={<Close />}
                onClick={() => onAnswer?.(false)}
              >
                <FormattedMessage {...messages.NO} />
              </Button>
            </div>
          </ComponentSwitch.Item>

          <ComponentSwitch.Item value>
            <Button
              icon={<Close />}
              onClick={onRequestClose}
              aria-label={intl.formatMessage(globalMessages.CLOSE)}
              css={styles.closeButton}
              size={Button.SIZES.SMALL}
            />

            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD} css={styles.title}>
              <FormattedMessage
                {...messages.THANKS_REGISTERED}
                values={{
                  email: proofEmail,
                  a: (chunks: ReactNode) => (
                    <a href={`mailto:${proofEmail}`} css={styles.emailLink}>
                      {chunks}
                    </a>
                  ),
                }}
              />
            </Typography>
          </ComponentSwitch.Item>

          <ComponentSwitch.Item value={false}>
            <Button
              icon={<Close />}
              onClick={onRequestClose}
              aria-label={intl.formatMessage(globalMessages.CLOSE)}
              css={styles.closeButton}
              size={Button.SIZES.SMALL}
            />

            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD} css={styles.title}>
              <FormattedMessage {...messages.THANKS_NOT_REGISTERED} />
            </Typography>
          </ComponentSwitch.Item>
        </ComponentSwitch>
      </Card.Body>
    </Card>
  );
};

ExamRegistrationCardBase.displayName = 'ExamRegistrationCard';

export const ExamRegistrationCard = memo(ExamRegistrationCardBase);
